import React, { useState } from 'react';
import { forgotPassword } from '../services/api';

// PR-FP: the "I forgot my password" request screen. Reached from a link
// on the Login page. Collects an email, calls POST /auth/forgot-password,
// and shows a generic confirmation.
//
// The backend deliberately returns the same response whether or not the
// email is registered (no account enumeration), so this screen shows the
// same success message in every non-error case -- it never tells the user
// whether the address actually has an account.
function ForgotPassword({ onBackToLogin }) {
    const [email,     setEmail]     = useState('');
    const [error,     setError]     = useState('');
    const [loading,   setLoading]   = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            await forgotPassword(email);
            setSubmitted(true);
        } catch (err) {
            // A 4xx/5xx here is a real failure (validation, rate limit,
            // server error) -- distinct from the "email not found" case,
            // which the backend reports as a 200.
            setError(err.response?.data?.error || 'Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#13294f] flex items-center justify-center px-4">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <div className="flex items-center justify-center gap-4 mb-4">
                        <img src="/favicon.svg" alt="" className="h-20 w-20 flex-shrink-0" />
                        <div className="text-5xl font-black text-white tracking-tight">
                            Hebert<span className="text-orange-500">Hoops</span>
                        </div>
                    </div>
                    <p className="text-gray-400">Reset your password</p>
                </div>
                <div className="bg-gray-900 rounded-2xl p-8 border border-gray-800">
                    {submitted ? (
                        <div className="space-y-6">
                            <div className="bg-green-900/40 border border-green-600 text-green-200 px-4 py-3 rounded-lg text-sm">
                                If that email is registered, a password reset
                                link has been sent. The link expires in 3 hours.
                            </div>
                            <p className="text-gray-400 text-sm">
                                Check your inbox and follow the link to choose a
                                new password. If nothing arrives, double-check
                                the address or contact the pool admin.
                            </p>
                            <button
                                onClick={onBackToLogin}
                                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 rounded-lg transition-colors"
                            >
                                Back to sign in
                            </button>
                        </div>
                    ) : (
                        <>
                            {error && (
                                <div className="bg-red-900/50 border border-red-500 text-red-300 px-4 py-3 rounded-lg mb-6 text-sm">
                                    {error}
                                </div>
                            )}
                            <p className="text-gray-400 text-sm mb-6">
                                Enter the email address for your account and
                                we'll send you a link to reset your password.
                            </p>
                            <form onSubmit={handleSubmit} className="space-y-5">
                                <div>
                                    <label
                                        htmlFor="forgot-email"
                                        className="block text-sm font-medium text-gray-300 mb-2"
                                    >
                                        Email
                                    </label>
                                    <input
                                        id="forgot-email"
                                        type="email"
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                                        placeholder="you@example.com"
                                        autoCorrect="off"
                                        autoCapitalize="none"
                                        required
                                    />
                                </div>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-900 text-white font-bold py-3 rounded-lg transition-colors"
                                >
                                    {loading ? 'Sending...' : 'Send reset link'}
                                </button>
                            </form>
                            <div className="text-center mt-6 pt-6 border-t border-gray-800">
                                <button
                                    onClick={onBackToLogin}
                                    className="text-orange-400 hover:text-orange-300 text-sm font-medium"
                                >
                                    Back to sign in
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ForgotPassword;